import React from 'react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { Box, Button } from '@mui/material';
import ReuseableTable from '../../table/ReuseableTable';
import useGetWaivers from '../../../hooks/waivers/useGetWaivers';
import OutlinedPageBox from '../../global/OutlinedPageBox';
import CenteredContentLayout from '../../global/CenteredContentLayout';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import {
  CreateWaiverPayload,
  createWaiverSchema,
} from '../../../types/waivers';
import useUser from '../../../hooks/global/useUser';
import useNewWaiver from '../../../hooks/waivers/useNewWaiver';
import useSelectWaiver from '../../../hooks/waivers/useSelectWaiver';
import { LoadedUser, Row } from '../../../interfaces';
import TableDropdownMenu from '../../global/TableDropdownMenu';
import PageLoading from '../../global/PageLoading';
import useUpdateWaiver from '../../../hooks/waivers/useUpdateWaiver';
import useWaiverTableActions from '../../../hooks/waivers/useWaiverTableActions';
import useSubscriptionUpsell from '../../../hooks/subscriptions/useSubscriptionUpsell';
import { Feature } from '../../../types/subscriptions';

function WaiversTable() {
  const { data, isLoading } = useGetWaivers();
  const { user } = useUser<LoadedUser>();
  const { onSelectWaiver, defaultWaiver } = useSelectWaiver();
  const {
    editWaiver,
    viewWaiverSubmissions,
    renameWaiver,
    previewWaiver,
    archiveWaiver,
  } = useWaiverTableActions();
  const { requireUpsell, upsell } = useSubscriptionUpsell(Feature.WAIVERS);

  const [newWaiverForm, setNewWaiverForm] = React.useState<FormData | null>(
    null,
  );

  const { mutation: updateMutation } = useUpdateWaiver();
  const { mutation: newWaiverMutation, isPending: isPendingNew } = useNewWaiver(
    {
      onSuccess: newWaiver => {
        if (!newWaiver?.id) return;
        toast.success('Waiver created successfully');
        onSelectWaiver(newWaiver.id, { isNew: true });
      },
      onError: () => {
        toast.error('Failed to create waiver');
      },
    },
  );

  const hasNoUpdatedWaivers =
    !data?.options?.length ||
    (data?.options.length === 1 && !data?.options[0].hasChanges);

  const waiverForm: FormData<CreateWaiverPayload> = {
    initialValues: {
      businessId: user?.business?.id,
      name: '',
      serviceIds: [],
    },
    inputRows: [
      [
        {
          inputType: 'text',
          label: 'Waiver Name',
          placeholder: 'Enter waiver name',
          name: 'name',
          disabled: isPendingNew,
          tooltip: 'The name of the waiver. Must be unique.',
        },
      ],
    ],
    onSubmit: async values => {
      const newWaiver = await newWaiverMutation.mutateAsync(values);
      if (newWaiver?.id) {
        onSelectWaiver(newWaiver.id);
        setNewWaiverForm(null);
      }
    },
    title: 'Create a new waiver',
    submitButtonText: 'Create',
    validationSchema: createWaiverSchema,
  };

  const onOpenNewWaiverForm = () => {
    if (requireUpsell) {
      upsell();
      return;
    }
    setNewWaiverForm(waiverForm);
  };

  const rows = React.useMemo(() => {
    const draftOptions =
      data?.options.filter(option => option.status === 'DRAFT') ?? [];
    const tempRows: Row[] =
      draftOptions.map(option => {
        const publishedOption = data?.options.find(
          o => o.waiverName === option.waiverName && o.status === 'PUBLISHED',
        );
        const status = publishedOption ? 'Published' : 'Draft';
        return {
          rowId: option.waiverId,
          cells: [
            option.waiverName,
            status,
            publishedOption?.publishedAt
              ? format(new Date(publishedOption.publishedAt), 'MMMM d, yyyy')
              : 'Not published',
            <TableDropdownMenu
              options={
                [
                  { label: 'Edit', action: () => editWaiver(option.waiverId) },
                  {
                    label: 'View Submissions',
                    action: () => viewWaiverSubmissions(option.waiverId),
                  },
                  {
                    label: 'Preview',
                    action: () =>
                      previewWaiver({
                        draftId: option.waiverId,
                        publishedId: publishedOption?.waiverId,
                      }),
                  },
                  {
                    label: 'Rename',
                    action: () =>
                      renameWaiver({
                        id: option.waiverId,
                        name: option.waiverName,
                      }),
                  },
                  !option.isDefault && {
                    label: 'Archive',
                    action: () =>
                      archiveWaiver(
                        option.waiverId,
                        option.services.map(s => s.id),
                      ),
                  },
                ].filter(Boolean) as React.ComponentProps<
                  typeof TableDropdownMenu
                >['options']
              }
              disabled={false}
            />,
          ],
        };
      }) || [];
    return tempRows;
  }, [
    data?.options,
    editWaiver,
    previewWaiver,
    renameWaiver,
    viewWaiverSubmissions,
    archiveWaiver,
  ]);

  const onRowClick = (rowId: string) => {
    onSelectWaiver(rowId);
  };

  const onOpenDefaultWaiver = () => {
    if (requireUpsell) {
      upsell();
      return;
    }

    if (!defaultWaiver) return;
    updateMutation.mutate({
      waiverId: defaultWaiver.waiverId,
      update: true,
    });
    onSelectWaiver(defaultWaiver.waiverId);
  };

  return (
    <>
      {isLoading ? (
        <PageLoading />
      ) : hasNoUpdatedWaivers ? (
        <OutlinedPageBox>
          <CenteredContentLayout
            image={{
              src: '/waivers.svg',
              alt: 'Add an item',
            }}
            title="Create a liability waiver"
            description="Create your first liability waiver so that appointments through Porter automatically have waivers tied to them for clients to complete"
            button={{
              text: 'Create Your Waiver',
              onClick: onOpenDefaultWaiver,
            }}
            maxWidth="750px"
            imageMaxWidth="120px"
          />
        </OutlinedPageBox>
      ) : (
        <>
          <Box className="header-right">
            <Button
              variant="contained"
              className="primary"
              onClick={onOpenNewWaiverForm}>
              Create New Waiver
            </Button>
          </Box>
          <ReuseableTable
            name="Your Waivers"
            emptyMessage="No waivers have been created yet"
            headerCells={[
              { id: 'name', label: 'Waiver Name' },
              { id: 'status', label: 'Status' },
              { id: 'publishedAt', label: 'Last Published' },
              { id: 'action', label: '' },
            ]}
            loading={false}
            rows={rows}
            handleRowClick={onRowClick}
          />
        </>
      )}
      <FormSmallModal
        form={newWaiverForm}
        open={!!newWaiverForm}
        onClose={() => setNewWaiverForm(null)}
        alert={{
          title: 'New waiver',
          subtitle:
            'Create a new waiver if you need to have clients sign different waivers for different services.',
          dismissible: false,
          type: 'info',
        }}
      />
    </>
  );
}

export default WaiversTable;
